import { defaultCountries, parseCountry } from "react-international-phone";
import { getExampleNumber, parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";

export interface CountryPhoneDetails {
  placeholder: string;
  maxDigits: number;
}

/**
 * Validates any E.164 phone string (e.g. "+233241234567") globally using libphonenumber-js.
 */
export function isPhoneValid(phone: string): boolean {
  if (!phone || phone.trim().length < 7) return false;
  const phoneNumber = parsePhoneNumberFromString(phone);
  return phoneNumber ? phoneNumber.isValid() : false;
}

/**
 * Strips country dial code prefix from a stored phone string, returning subscriber digits.
 */
export function stripDialCode(phone: string, dialCode: string): string {
  if (!phone) return "";
  const withPlus = `+${dialCode}`;
  if (phone.startsWith(withPlus)) {
    return phone.slice(withPlus.length).trimStart();
  }
  const digitsOnly = phone.replace(/\D/g, "");
  if (digitsOnly.startsWith(dialCode)) {
    return digitsOnly.slice(dialCode.length);
  }
  return phone;
}

/**
 * Detects the ISO2 country code from an E.164 phone string (e.g. "+233..." -> "gh").
 */
export function detectCountryIso2(phone: string, fallbackCountryIso2 = "gh"): string {
  if (!phone || !phone.startsWith("+")) return fallbackCountryIso2;
  const parsed = parsePhoneNumberFromString(phone);
  if (parsed?.country) {
    return parsed.country.toLowerCase();
  }
  const digits = phone.replace(/\D/g, "");
  const sorted = [...defaultCountries]
    .map((c) => parseCountry(c))
    .sort((a, b) => b.dialCode.length - a.dialCode.length);
  for (const c of sorted) {
    if (digits.startsWith(c.dialCode)) return c.iso2;
  }
  return fallbackCountryIso2;
}

/**
 * Dynamically computes national format example placeholder and maximum allowed subscriber digits
 * for any given ISO2 country code using ITU metadata (no hardcoding).
 */
export function getCountryPhoneDetails(iso2: string): CountryPhoneDetails {
  const upperIso = iso2.toUpperCase() as CountryCode;
  try {
    const example = getExampleNumber(upperIso, examples);
    if (example) {
      const national = example.formatNational(); // e.g. "024 123 4567" or "(202) 555-0123"
      const digitsOnly = national.replace(/\D/g, "");
      const maxDigits = national.startsWith("0") ? digitsOnly.length - 1 : digitsOnly.length;
      return { placeholder: national, maxDigits: maxDigits || 10 };
    }
  } catch {
    // Fallback if metadata unavailable
  }
  return { placeholder: "Enter phone number", maxDigits: 10 };
}

/**
 * Strips leading local trunk zero (e.g. "0241234567" -> "241234567") and enforces exact max subscriber digits.
 */
export function sanitizeSubscriberDigits(digits: string, maxDigits: number): string {
  if (!digits) return "";
  const cleaned = digits.replace(/\D/g, "");
  if (cleaned.length > 1 && cleaned.startsWith("0")) {
    return cleaned.slice(1).slice(0, maxDigits);
  }
  return cleaned.slice(0, maxDigits);
}
